<template>
  <BottomSheet
    v-if="!opened"
    ref="sheet"
    fit
    close-position="end"
    title="Settings"
    data-cy="schedule-settings-sheet"
    @hide-sheet="onSheetHidden"
  >
    <div class="flex flex-col gap-3 p-4">
      <ScheduleSettingsCard
        title="Schedule key"
        status="What the colors and shapes mean"
        data-cy="schedule-settings-key"
        @select="choose('key')"
      >
        <template #icon><i class="fa-regular fa-circle-info" /></template>
      </ScheduleSettingsCard>
      <ScheduleSettingsCard
        title="Appointment status filter"
        :status="filterSummary"
        data-cy="schedule-settings-filter"
        @select="choose('filter')"
      >
        <template #icon><i class="fa-regular fa-filter" /></template>
      </ScheduleSettingsCard>
      <ScheduleSettingsCard
        title="Check availability"
        status="Find an open slot"
        data-cy="schedule-settings-availability"
        @select="choose('availability')"
      >
        <template #icon><i class="fa-regular fa-calendar" /></template>
      </ScheduleSettingsCard>
      <ScheduleSettingsCard
        title="Use Old Scheduler"
        status="Phones only. Desktop is unchanged"
        data-cy="schedule-settings-old-scheduler"
        @select="choose('old_scheduler')"
      >
        <template #icon><RevertIcon class="h-4" /></template>
      </ScheduleSettingsCard>
    </div>
  </BottomSheet>

  <ScheduleLegendSheet v-else-if="opened === 'key'" @close="emit('close')" />

  <Modal
    v-else-if="opened === 'filter'"
    title="Filter"
    no-footer
    @hide-modal="emit('close')"
  >
    <ScheduleFilterMenu />
  </Modal>

  <CheckAvailabilityModal
    v-else-if="opened === 'availability'"
    @hide-modal="emit('close')"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { trackMobileWebEvent } from "@/lib/pendo";

import Modal from "@/components/Modal.vue";
import BottomSheet from "@/components/Seranova/BottomSheet/BottomSheet.vue";
import CheckAvailabilityModal from "@/CheckAvailabilityModal/CheckAvailabilityModal.vue";
import RevertIcon from "@/images/ArrowUUpLeft.svg?component";
import { useSchedulerChoice } from "@/SchedulerV2/useSchedulerChoice";
import ScheduleFilterMenu from "./ScheduleFilterMenu.vue";
import ScheduleLegendSheet from "./ScheduleLegendSheet.vue";
import ScheduleSettingsCard from "./ScheduleSettingsCard.vue";
import { statusFilterSummary } from "./statusFilterSummary";

// The scheduler's settings menu on a phone: the three destinations the gear
// gathers, whichever one the tap opened, and the switch back to Old Scheduler.
// The filter and availability surfaces are the existing modals rendered as
// they are -- this menu gives them an entry point, it does not change them.
type Destination = "key" | "filter" | "availability";
type SettingsItem = Destination | "old_scheduler";

const store = useStore();
const { switchTo } = useSchedulerChoice();
const sheet = ref<InstanceType<typeof BottomSheet> | null>(null);
const opened = ref<Destination | null>(null);
// Not a ref: nothing renders from it. It only carries the choice across the two
// handlers below, between the tap and the sheet finishing its leave transition.
let pending: Destination | null = null;

const emit = defineEmits<{
  (e: "close"): void;
}>();

const filterSummary = computed(() =>
  statusFilterSummary(store.state.timeline.filters.statuses)
);

// Closing through the sheet's own close() rather than swapping the v-if
// underneath it: the panel is a <Transition> child, so unmounting it mid-flight
// would skip the leave animation and swallow the hide-sheet this handler needs.
const choose = (item: SettingsItem) => {
  trackMobileWebEvent("schedule_settings_item_selected", {
    surface: "scheduler",
    item
  });
  // A switch, not a destination: the page reloads into Old Scheduler, so there
  // is nothing to open once the sheet has closed and it simply stays until
  // the reload replaces it.
  if (item === "old_scheduler") {
    switchTo("old_scheduler", "settings");
    return;
  }
  pending = item;
  sheet.value?.close();
};

// The sheet closes for two different reasons and only one of them opens
// something: a tap on a card, or a dismissal (close icon, backdrop, Escape,
// swipe). With nothing pending the whole menu is done.
const onSheetHidden = () => {
  if (!pending) return emit("close");
  opened.value = pending;
  pending = null;
};
</script>
